import React, { useEffect, useRef, useState } from "react";
import "../../assets/fa-icons/css/fontawesome.css";
import "../../assets/fa-icons/css/sharp-regular.css";
import "../../assets/fa-icons/css/sharp-solid.css";
import "../../assets/fa-icons/css/brands.css";
import { FontStyles } from "../utils/font-styles.ts";
import { AddressTextDisplay } from "./address-text-display.tsx";
import { cn } from "../utils/cn.ts";
import { useWatchlist } from "./utils/use-watchlist.ts";
import { usePropertyDetailsContext } from "./contexts/property-details-context.tsx";
import { TrackEvent } from "../utils/analytics.ts";
import { Sharing, type ShareMethod } from "../components/sharing.tsx";
import { focusStyles, focusStylesOutline } from "../utils/focus-styles.ts";

/**
 * Offset for elements that stick directly below the TopBar on desktop: the bar's height,
 * py-6 (48px) plus the 20px text-xl icon line (leading-none). Keep in sync with the classes below.
 */
export const TOP_BAR_STICKY_OFFSET_PX = 68;

const iconStyles = "text-xl fa-fw content-center flex-none";
const innerContainerStyles =
  "flex justify-between max-w-espc-max-content mx-3.5 md:mx-6 w-full " +
  `${FontStyles["body-l"]} font-medium leading-none`;

const trackShare = (method: ShareMethod) => {
  TrackEvent({ name: "share", value: 10, content_type: "property_details", method });
};

const topbarButtonStyles = cn("leading-none", focusStyles, "hover:bg-north-sea-600 h-full px-1.5 flex gap-2");

export const TopBar: React.FC = () => {
  const { propertyId, propertySource, backLink, shareUrl } = usePropertyDetailsContext();
  const { loading, isWatchlisted, toggle } = useWatchlist(propertyId, propertySource, "property_details");
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  const [isShareExpanded, setIsShareExpanded] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), { threshold: 0 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" />
      <div
        className={cn(
          "flex justify-center text-neutral-white bg-north-sea sticky top-0 z-[1001]",
          isStuck && "shadow-lg",
        )}
      >
        <div className={innerContainerStyles}>
          <div className={"flex gap-2 lg:gap-3 min-w-0 items-center py-4 md:py-6"}>
            <a
              href={backLink}
              className={cn("group flex items-center flex-none cursor-pointer", focusStylesOutline)}
              aria-label="Back to property search"
            >
              <i
                aria-hidden="true"
                className={`${iconStyles} fa-sharp fa-solid fa-angle-left text-loch-400 group-hover:text-loch-500 group-active:text-loch-600`}
              />
            </a>
            <AddressTextDisplay className={"leading-none"} />
          </div>
          <div
            className={cn(
              "flex ml-2.5 p-0 m-0",
              // Expanded: 10px between the social buttons on mobile, 12px from tablet up.
              isShareExpanded ? "gap-2.5 md:gap-3" : "gap-3.5 md:gap-8",
            )}
          >
            <Sharing
              url={shareUrl}
              expandable
              isExpanded={isShareExpanded}
              onExpandedChange={setIsShareExpanded}
              onShare={trackShare}
              className={topbarButtonStyles}
              iconClassName={cn(iconStyles, "text-neutral-white md:text-loch-400 font-normal")}
              nativeShareLabel={<span className={"hidden md:flex items-center"}>Share</span>}
            />
            <button
              aria-label={isWatchlisted ? "Remove from watchlist" : "Add to watchlist"}
              className={cn(topbarButtonStyles, loading ? "animate-pulse" : "")}
              disabled={loading}
              onClick={() => void toggle()}
            >
              <i
                aria-hidden="true"
                className={cn(
                  `fa-sharp fa-heart flex-none pb-0.5 text-neutral-white md:text-loch-400`,
                  iconStyles,
                  isWatchlisted ? "fa-solid" : "fa-regular",
                )}
              />
              <span className="hidden md:flex items-center">{isWatchlisted ? "Saved" : "Save"}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
